import driveWordmark from "~/assets/products/hero/drive-wordmark.svg";
import type { FleetProductPageContent, ProductFeatureCategory } from "./types";
import { DRIVE_PRODUCT } from "./drive";
import { imageUrl } from "~/lib/image-url";
import { WISTIA_MEDIA_IDS } from "~/lib/wistia";

const highlightVehicleTracking = imageUrl("/images/products/drive-fleet/highlights/vehicle-tracking.png");
const highlightGeoZones = imageUrl("/images/products/drive-fleet/highlights/geo-zones.png");
const highlightDrivingHabits = imageUrl("/images/products/drive-fleet/highlights/driving-habits.png");
const highlightRouteHistory = imageUrl("/images/products/drive-fleet/highlights/route-history.png");
const highlightVehicleCare = imageUrl("/images/products/drive-fleet/highlights/vehicle-care.png");
const highlightReporting = imageUrl("/images/products/drive-fleet/highlights/reporting.png");

function highlight(key: string, src: string) {
  const prefix = `drive_fleet.highlights.items.${key}`;
  return { title: `${prefix}.title`, description: `${prefix}.description`, image: { src, alt: `${prefix}.imageAlt` } };
}

function featureCategory(key: string, cardCount: number): ProductFeatureCategory {
  return {
    label: `drive_fleet.features.categories.${key}.label`,
    cards: Array.from({ length: cardCount }, (_, i) => ({
      title: `drive_fleet.features.categories.${key}.cards.${i}.title`,
      description: `drive_fleet.features.categories.${key}.cards.${i}.description`,
    })),
  };
}

// Fleet Drive PDP (BNC-5702, Figma 10480:407808 mobile / 10480:408565 desktop).
// Copy fields hold i18next keys under products.json's "drive_fleet" block, never the
// consumer "drive" keys, so a consumer copy edit can't change the fleet page.
export const DRIVE_FLEET_PRODUCT: FleetProductPageContent = {
  slug: "drive",
  productCode: "bouncieobd",
  name: "Drive",
  metaTitle: "drive_fleet.metaTitle",
  metaDescription: "drive_fleet.metaDescription",
  hero: {
    image: { src: imageUrl("/images/products/drive-fleet/hero/fleet-box.png"), alt: "drive_fleet.hero.imageAlt" },
    imageBadge: "drive_fleet.hero.imageBadge",
    rating: null,
    reviewCount: null,
    tagline: "drive_fleet.hero.tagline",
    productDescriptor: "drive_fleet.hero.productDescriptor",
    wordmark: driveWordmark,
    subscription: {
      headline: "drive_fleet.hero.subscription.headline",
      body: "drive_fleet.hero.subscription.body",
    },
    valueProps: [
      "drive_fleet.hero.valueProps.noContracts",
      "drive_fleet.hero.valueProps.noHiddenFees",
      "drive_fleet.hero.valueProps.noActivationFees",
    ],
    quoteBuilder: {
      sizeLabel: "drive_fleet.hero.quoteBuilder.sizeLabel",
      totalLabel: "drive_fleet.hero.quoteBuilder.totalLabel",
      devicesLabel: "drive_fleet.hero.quoteBuilder.devicesLabel",
      emailLabel: "drive_fleet.hero.quoteBuilder.emailLabel",
      submitLabel: "drive_fleet.hero.quoteBuilder.submitLabel",
      moreLabel: "drive_fleet.hero.quoteBuilder.moreLabel",
      moreCtaLabel: "drive_fleet.hero.quoteBuilder.moreCtaLabel",
    },
  },
  // Six items, none shared with the consumer highlights. Images are the flattened 3x Figma exports.
  highlights: {
    eyebrow: "drive_fleet.highlights.eyebrow",
    heading: "drive_fleet.highlights.heading",
    description: "drive_fleet.highlights.description",
    items: [
      highlight("vehicleTracking", highlightVehicleTracking),
      highlight("geoZones", highlightGeoZones),
      highlight("drivingHabits", highlightDrivingHabits),
      highlight("routeHistory", highlightRouteHistory),
      highlight("vehicleCare", highlightVehicleCare),
      highlight("reporting", highlightReporting),
    ],
  },
  // Same shape as the consumer grid, plus Reports & Exports. The mobile comp's first two
  // descriptions are placeholders/misplaced, so all copy follows the desktop frames.
  // "See All Features" goes to the fleet all-features page (BNC-5712).
  features: {
    eyebrow: "drive_fleet.features.eyebrow",
    headline: "drive_fleet.features.headline",
    ctaLabel: "drive_fleet.features.cta",
    ctaHref: "/fleet/products/drive/features",
    categories: [
      featureCategory("gps_routes", 6),
      featureCategory("driving_insights", 6),
      featureCategory("safety_security", 6),
      featureCategory("diagnostics_care", 6),
      featureCategory("access_users", 3),
      featureCategory("reports_exports", 3),
    ],
  },
  promo: {
    headline: "drive_fleet.promo.headline",
    body: "drive_fleet.promo.body",
    image: imageUrl("/images/products/drive-fleet/view-promo.png"),
    imageAlt: "drive_fleet.promo.imageAlt",
    ctaLabel: "drive_fleet.promo.ctaLabel",
    ariaLabel: "drive_fleet.promo.ariaLabel",
    ctaHref: "/products/view",
  },
  // Poster is FPO in Figma (no asset yet). The clip is the plan's candidate, already used
  // on /fleet — design/marketing still to confirm it, or supply a new one.
  video: {
    headline: "drive_fleet.video.headline",
    ctaLabel: "drive_fleet.video.ctaLabel",
    ariaLabel: "drive_fleet.video.ariaLabel",
    wistiaId: WISTIA_MEDIA_IDS.transformingFleetVehicles,
  },
  // Same device, so the same spec items and image as the consumer PDP; only the headline
  // differs. Figma marks the exploded render FPO; swap `image` when the final one arrives.
  specs: { ...DRIVE_PRODUCT.specs, headline: "drive_fleet.specs.headline" },
  contractBanner: {
    headline: "drive_fleet.contractBanner.headline",
    body: "drive_fleet.contractBanner.body",
    badge: "drive_fleet.contractBanner.badge",
    ctaLabel: "drive_fleet.contractBanner.ctaLabel",
    ctaHref: "/contact",
  },
  // Ten fleet Q&As from Figma's FAQ frame. #3 (lifetime warranty) and #10 (permissions
  // wording) still need product sign-off before launch.
  faq: {
    headline: "drive_fleet.faq.headline",
    items: [
      { question: "drive_fleet.faq.items.get_started.question", answer: "drive_fleet.faq.items.get_started.answer" },
      { question: "drive_fleet.faq.items.contract.question", answer: "drive_fleet.faq.items.contract.answer" },
      { question: "drive_fleet.faq.items.replacement.question", answer: "drive_fleet.faq.items.replacement.answer" },
      { question: "drive_fleet.faq.items.fleet_types.question", answer: "drive_fleet.faq.items.fleet_types.answer" },
      { question: "drive_fleet.faq.items.driver_behavior.question", answer: "drive_fleet.faq.items.driver_behavior.answer" },
      { question: "drive_fleet.faq.items.tamper.question", answer: "drive_fleet.faq.items.tamper.answer" },
      { question: "drive_fleet.faq.items.reports.question", answer: "drive_fleet.faq.items.reports.answer" },
      { question: "drive_fleet.faq.items.support.question", answer: "drive_fleet.faq.items.support.answer" },
      { question: "drive_fleet.faq.items.geo_zones.question", answer: "drive_fleet.faq.items.geo_zones.answer" },
      { question: "drive_fleet.faq.items.roles.question", answer: "drive_fleet.faq.items.roles.answer" },
    ],
  },
};
